// ENG-11 — save-format freeze (M25). The v1.0 save format is frozen: every
// persisted world/player record is stamped with CURRENT_SAVE_VERSION, and any
// record from an earlier era passes through this pure migration harness on load
// so it still opens. The harness only ever *adds* the fields a newer format
// introduced — filling them with the same defaults the runtime already used as
// fallbacks — and re-stamps the version; it never drops data it doesn't
// recognise. Changing the format after the freeze means: bump
// CURRENT_SAVE_VERSION, add a step to `migrateWorldMeta`/`migratePlayerState`,
// and commit a frozen fixture (see tests/migrate.test.ts) proving the old shape
// still loads.
//
// Pure — no IndexedDB, no DOM — so vitest can exercise every save era directly.
import type { WorldMeta, WorldType, PlayerState, WorldProgress } from './saves';
import type { StatsSave } from '../../sim/game/stats';
import type { AdvancementSave } from '../../sim/game/advancements';
import { DEFAULT_RANGE, type YRange } from '../../sim/engine/section';
import { SAVE_VERSION } from '../../sim/engine/saves';
import { sanitizeNotebook } from '../../sim/heredity/notebook';
import { sanitizeLessons, sanitizeWorldLesson, sanitizeLessonLog } from '../../sim/education/results';

/** The frozen save-format version. v1 = the M25 freeze; v2 = Part VII / TAL-4
 *  (section rows keyed by absolute signed section-Y + per-dim ranges in the world
 *  row); v3 = Part VIII / M45.2 (`Entity.scriptMem`, ARCH §27.7); v4 = Part IX / M48.6
 *  (heredity: entity genome/parents/name/id, the `crop` block entity, the player's
 *  notebook — ARCH §28.12, every field optional); v5 = Part X / M51.5 (`WorldMeta.
 *  lesson`/`lessonLog`, `PlayerState.lessons` — ARCH §29.13, every field optional).
 *  Bumping this requires a migration step + a committed fixture (ENG-11).
 *
 *  The number itself lives in `sim/engine/saves.ts` — the sim owns the format the
 *  headless host and the browser both write, so there is exactly one of it. */
export const CURRENT_SAVE_VERSION = SAVE_VERSION;

/**
 * Section rows saved before v2 keyed sections by their offset-baked array index
 * (`sy = (y>>4) + range.sectionYOffset`), which silently reindexes if a
 * dimension's MIN_Y ever changes. v2 keys them by **absolute signed section-Y**
 * (`sy = y>>4`), range-independent (TAL-4). These pure helpers convert between the
 * two given the dimension's range (default range ⇒ a fixed −4 shift, D28).
 */
export function absoluteSectionY(arrayIndexSy: number, range: YRange = DEFAULT_RANGE): number {
  return arrayIndexSy - range.sectionYOffset;
}
export function sectionArrayIndex(absoluteSy: number, range: YRange = DEFAULT_RANGE): number {
  return absoluteSy + range.sectionYOffset;
}

const WORLD_TYPES: readonly WorldType[] = ['normal', 'flat', 'atlas', 'earth'];
const GAME_MODES = ['survival', 'creative'] as const;
const DIFFICULTIES = ['peaceful', 'easy', 'normal', 'hard'] as const;

type Dict = Record<string, unknown>;
const isObj = (v: unknown): v is Dict => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown, dflt: string): string => (typeof v === 'string' ? v : dflt);
const num = (v: unknown, dflt: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : dflt);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[], dflt: T): T =>
  (typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : dflt);

/** An empty progression bundle — what a fresh (or pre-M25) world loads with. */
export function emptyProgress(): WorldProgress {
  return {
    version: CURRENT_SAVE_VERSION,
    stats: { custom: [], categories: [] },
    advancements: { done: [], criteria: [] },
    recipeBook: [],
  };
}

/** Coerce a persisted stats blob to a well-formed `StatsSave` (the tracker's
 *  `load` is itself defensive, but a bad blob shouldn't throw here first). */
function normStats(v: unknown): StatsSave {
  if (!isObj(v)) return { custom: [], categories: [] };
  const pairs = (a: unknown): (readonly [string, number])[] =>
    Array.isArray(a) ? a.filter((p) => Array.isArray(p) && p.length >= 2).map((p) => [String(p[0]), num(p[1], 0)] as const) : [];
  const custom = pairs(v['custom']);
  const categories = Array.isArray(v['categories'])
    ? v['categories']
        .filter((c) => Array.isArray(c) && c.length >= 2)
        .map((c) => [String(c[0]), pairs(c[1])] as const)
    : [];
  return { custom, categories };
}

function normAdvancements(v: unknown): AdvancementSave {
  if (!isObj(v)) return { done: [], criteria: [] };
  const done = Array.isArray(v['done']) ? v['done'].filter((s) => typeof s === 'string') : [];
  const criteria = Array.isArray(v['criteria'])
    ? v['criteria']
        .filter((c) => Array.isArray(c) && c.length >= 2)
        .map((c) => [String(c[0]), Array.isArray(c[1]) ? c[1].filter((s) => typeof s === 'string') : []] as const)
    : [];
  return { done, criteria };
}

/** Normalise a persisted progression blob (absent on every pre-M25 save). */
export function normalizeProgress(v: unknown): WorldProgress {
  if (!isObj(v)) return emptyProgress();
  return {
    version: num(v['version'], CURRENT_SAVE_VERSION),
    stats: normStats(v['stats']),
    advancements: normAdvancements(v['advancements']),
    recipeBook: Array.isArray(v['recipeBook']) ? v['recipeBook'].filter((s) => typeof s === 'string') : [],
  };
}

/**
 * Migrate any persisted world record to the current shape. Returns null only for
 * a record with no usable id (unrecoverable); everything else is filled with the
 * era-appropriate defaults the runtime already assumed and re-stamped to
 * CURRENT_SAVE_VERSION.
 */
export function migrateWorldMeta(raw: unknown): WorldMeta | null {
  if (!isObj(raw) || typeof raw['id'] !== 'string' || raw['id'] === '') return null;
  const worldType = oneOf<WorldType>(raw['worldType'], WORLD_TYPES, 'normal');
  const gameMode = oneOf(raw['gameMode'], GAME_MODES, 'creative');
  const now = Date.now();
  const meta: WorldMeta = {
    id: raw['id'],
    name: str(raw['name'], 'World'),
    seed: str(raw['seed'], '0'),
    worldType,
    gameMode,
    difficulty: oneOf(raw['difficulty'], DIFFICULTIES, 'normal'),
    // pre-M5 saves have no allowCommands; the runtime fell back to "cheats iff not
    // survival", so migration bakes that in rather than leaving it undefined.
    allowCommands: typeof raw['allowCommands'] === 'boolean' ? raw['allowCommands'] : gameMode !== 'survival',
    createdAt: num(raw['createdAt'], now),
    playedAt: num(raw['playedAt'], now),
    version: CURRENT_SAVE_VERSION,
  };
  if (typeof raw['hardcore'] === 'boolean') meta.hardcore = raw['hardcore'];
  if (isObj(raw['atlas'])) meta.atlas = raw['atlas'] as WorldMeta['atlas'];
  if (isObj(raw['earth'])) meta.earth = raw['earth'] as unknown as WorldMeta['earth'];
  if (Array.isArray(raw['overlays'])) meta.overlays = raw['overlays'] as WorldMeta['overlays'];
  if (isObj(raw['imageryBake'])) meta.imageryBake = raw['imageryBake'] as unknown as WorldMeta['imageryBake'];
  // World doors (M41): bounded footprint, parent-chain home, and pending return doors.
  if (isObj(raw['bounds'])) meta.bounds = raw['bounds'] as WorldMeta['bounds'];
  if (typeof raw['homeWorldId'] === 'string') meta.homeWorldId = raw['homeWorldId'];
  if (Array.isArray(raw['pendingDoors'])) meta.pendingDoors = raw['pendingDoors'] as WorldMeta['pendingDoors'];
  if (typeof raw['flatPreset'] === 'string') meta.flatPreset = raw['flatPreset'];
  // Per-dimension Y-ranges (v2 / TAL-4). Absent on v1 saves → the default range
  // (recorded lazily on the next save); a valid recorded map is preserved as-is.
  if (isObj(raw['ranges'])) {
    const ranges: Record<string, { minY: number; height: number }> = {};
    for (const [dim, r] of Object.entries(raw['ranges'])) {
      if (isObj(r) && typeof r['minY'] === 'number' && typeof r['height'] === 'number') {
        ranges[dim] = { minY: r['minY'], height: r['height'] };
      }
    }
    if (Object.keys(ranges).length) meta.ranges = ranges;
  }
  if (isObj(raw['gamerules'])) {
    const rules: Record<string, string> = {};
    for (const [k, val] of Object.entries(raw['gamerules'])) if (typeof val === 'string') rules[k] = val;
    meta.gamerules = rules;
  }
  // v5 (ARCH §29.13): the active lesson + its console log, optional — absent on
  // every older save, and sanitized like the rest of an untrusted row.
  const lesson = sanitizeWorldLesson(raw['lesson']);
  if (lesson) meta.lesson = lesson;
  const lessonLog = sanitizeLessonLog(raw['lessonLog']);
  if (lessonLog) meta.lessonLog = lessonLog;
  // v6 (ARCH §30.6, COW-10): the shared-screen policy gate. Absent on every
  // older save → undefined → `canShare` treats it as the default `false`.
  if (typeof raw['adultsOnly'] === 'boolean') meta.adultsOnly = raw['adultsOnly'];
  return meta;
}

/**
 * Migrate a persisted player record to the current shape. Returns undefined when
 * there is no record (fresh spawn); otherwise coordinates/dim are coerced,
 * `survival` is passed through (the sim's `loadJSON` is defensive), and
 * `progress` is normalised (absent on every pre-M25 save → empty).
 */
export function migratePlayerState(raw: unknown): PlayerState | undefined {
  if (!isObj(raw)) return undefined;
  const state: PlayerState = {
    x: num(raw['x'], 0),
    y: num(raw['y'], 0),
    z: num(raw['z'], 0),
    yaw: num(raw['yaw'], 0),
    pitch: num(raw['pitch'], 0),
    dim: str(raw['dim'], 'overworld'),
    progress: normalizeProgress(raw['progress']),
  };
  if (raw['survival'] !== undefined) state.survival = raw['survival'] as PlayerState['survival'];
  // v4 (ARCH §28.12): the field notebook, optional — absent on every older save, and
  // sanitized like the rest of an untrusted row.
  const notebook = sanitizeNotebook(raw['notebook']);
  if (notebook) state.notebook = notebook;
  // v5 (ARCH §29.6): per-lesson results, optional — absent on every older save.
  const lessons = sanitizeLessons(raw['lessons']);
  if (lessons) state.lessons = lessons;
  return state;
}
